import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import {
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  CircularProgress,
} from '@material-ui/core';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon, ArrowBack } from '@material-ui/icons';
import { LifeCheckRecord } from '../../../constants/Records';
import DeleteConfirmDialog from '../common/DeleteConfirmDialog';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      padding: theme.spacing(3),
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: theme.spacing(2),
    },
    breadcrumb: {
      color: theme.palette.text.secondary,
      marginBottom: theme.spacing(1),
    },
    tableContainer: {
      marginTop: theme.spacing(2),
    },
    actionCell: {
      width: 120,
    },
    emptyMessage: {
      textAlign: 'center',
      padding: theme.spacing(4),
      color: theme.palette.text.secondary,
    },
    loadingContainer: {
      display: 'flex',
      justifyContent: 'center',
      padding: theme.spacing(4),
    },
    backButton: {
      marginRight: theme.spacing(2),
    },
  }),
);

interface Props {
  institutionId: string;
  institutionName: string;
  userId: string;
  userName: string;
  uniqueId: string;
  records: LifeCheckRecord[];
  executing: boolean;
  loaded: boolean;
  deleted: boolean;
  onFetch: (uniqueId: string) => void;
  onDelete: (uniqueId: string, sequence: number) => void;
  onResetFlags: () => void;
}

const LifeCheckRecordList: React.FC<Props> = ({
  institutionId,
  institutionName,
  userId,
  userName,
  uniqueId,
  records,
  executing,
  loaded,
  deleted,
  onDelete,
  onResetFlags,
}) => {
  const classes = useStyles();
  const history = useHistory();

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<LifeCheckRecord | null>(null);

  React.useEffect(() => {
    if (deleted) {
      onResetFlags();
      setDeleteDialogOpen(false);
      setSelectedRecord(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleted]);

  const handleBack = () => {
    history.push(`/institutions/${institutionId}/users/${userId}/records`);
  };

  const handleCreate = () => {
    history.push(`/institutions/${institutionId}/users/${userId}/records/lifecheck/create`);
  };

  const handleEdit = (record: LifeCheckRecord) => {
    history.push(`/institutions/${institutionId}/users/${userId}/records/lifecheck/${record.sequence}/edit`);
  };

  const handleDeleteClick = (record: LifeCheckRecord) => {
    setSelectedRecord(record);
    setDeleteDialogOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedRecord(null);
  };

  const handleDeleteConfirm = () => {
    if (selectedRecord && uniqueId) {
      onDelete(uniqueId, selectedRecord.sequence);
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return dateStr.split('T')[0];
  };

  return (
    <div className={classes.root}>
      <Typography variant="body2" className={classes.breadcrumb}>
        {institutionName} &gt; {userName} &gt; 生活機能チェック
      </Typography>

      <div className={classes.header}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <IconButton className={classes.backButton} onClick={handleBack}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h5">生活機能チェック一覧</Typography>
        </div>
        <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleCreate}>
          新規作成
        </Button>
      </div>

      {executing && !loaded ? (
        <div className={classes.loadingContainer}>
          <CircularProgress />
        </div>
      ) : records.length === 0 ? (
        <Paper className={classes.emptyMessage}>
          <Typography>生活機能チェック記録はありません</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} className={classes.tableContainer}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>No.</TableCell>
                <TableCell>担当者</TableCell>
                <TableCell>訪問日時</TableCell>
                <TableCell>作成日</TableCell>
                <TableCell className={classes.actionCell}>操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {records.map(record => (
                <TableRow key={record.sequence}>
                  <TableCell>{record.sequence}</TableCell>
                  <TableCell>{record.staff || '-'}</TableCell>
                  <TableCell>{record.visited_time_start || '-'}</TableCell>
                  <TableCell>{formatDate(record.created_at)}</TableCell>
                  <TableCell className={classes.actionCell}>
                    <IconButton size="small" onClick={() => handleEdit(record)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDeleteClick(record)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        title="生活機能チェックの削除"
        content={`生活機能チェック No.${selectedRecord?.sequence} を削除しますか？`}
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        executing={executing}
      />
    </div>
  );
};

export default LifeCheckRecordList;
